/**
 * Rupee formatting.
 *
 * Money arrives from the API as a decimal string, never a number, so that a
 * rupee amount does not round-trip through a float on its way to the screen.
 * This formats the string directly and never parses it.
 *
 * Mirrors format_inr in backend/app/core/money.py, including Indian digit
 * grouping — last three digits, then pairs, so 1234567.89 reads 12,34,567.89.
 */
export function money(value: string | null | undefined): string {
  if (value == null) return '—';
  const [whole, fraction = '00'] = value.split('.');
  const sign = whole.startsWith('-') ? '-' : '';
  const digits = whole.replace('-', '');
  const head = digits.slice(0, -3);
  const tail = digits.slice(-3);
  const grouped = head
    ? `${head.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${tail}`
    : tail;
  return `${sign}₹${grouped}.${fraction.padEnd(2, '0').slice(0, 2)}`;
}

/**
 * A whole-rupee figure, grouped the Indian way and with no paise.
 *
 * For the places a rupee amount is a *choice* rather than an account: a salary
 * band in a dropdown, a figure on a badge. "₹15,000.00" in a list of thirty
 * options is four characters of noise per row, and every one of these values
 * is a round thousand by construction.
 *
 * Takes a number, unlike money() above, which deliberately never parses the
 * decimal strings the API sends. These figures are the form's own — generated
 * from a step, not read from a column — so there is no precision to lose.
 */
export function rupeesWhole(value: number): string {
  const digits = String(Math.round(Math.abs(value)));
  const head = digits.slice(0, -3);
  const tail = digits.slice(-3);
  const grouped = head
    ? `${head.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${tail}`
    : tail;
  return `${value < 0 ? '-' : ''}₹${grouped}`;
}
